import { Type } from '@angular/core';

import { Mercator } from '@amcharts/amcharts4/.internal/charts/map/projections/Mercator';
import * as am4core from '@amcharts/amcharts4/core';
import { HeatLegend, Legend, MapChart, MapLineSeries, MapPolygonSeries, ZoomControl } from '@amcharts/amcharts4/maps';

import am4geodata_worldLow from '@amcharts/amcharts4-geodata/worldLow';
import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';
import { LabelDisplayOptionsEnum } from '../../Misc/enums/label-display-options.enum';
import { defaultSeriesColorConfig } from '../config/default-series-color.config';
import { chartColors } from '../enums/chart-colors.enum';
import { manualChartExport } from '../helpers/manualChartExport.helper';
import { AbstractChartOptionsModel } from './abstract-chart-options.model';
import { ChartMetadataModel } from './chart-metadata.model';
import { ChartModel } from './chart.model';
import { CustomLegendItem } from './custom-legend-item.model';
import { DataPointModel } from './data-point.model';
import { mapChartAvailableCountryIsoCodes } from './map-chart-available-regions.const';
import { MapChartOptionsModel } from './map-chart.options.model';
import { SeriesModel } from './series.model';
import { ChartSizeHelper } from '../helpers/chart-size.helper';
import { numberWithSeparators } from '../../../Common/Misc/helpers/formatting/number.helper';


export enum MapChartColorSchemeEnum {
    CHOROPLETH = 'CHOROPLETH',
    REGION_COLORS = 'REGION_COLORS'
}

export enum MapChartChoroplethInterpolationTypeEnum {
    LINEAR = 'LINEAR',
    LOGARITHMIC = 'LOGARITHMIC'
}

export class MapChartModel extends ChartModel {
    readonly chartType: Type<MapChart> = MapChart;
    readonly singleSeries: boolean = true;
    protected footer: am4core.Container;

    private countryGeoData: any;
    protected countrySeries: MapPolygonSeries;
    protected countrySpaceSeries: MapLineSeries;
    protected worldSeries: MapPolygonSeries;
    outputSeries: MapPolygonSeries[] = [];
    homeButton: am4core.Button;
    min: number;
    max: number;
    heatLegendContainer: am4core.Container;
    heatLegend: HeatLegend;
    heatLegendLabel: am4core.Label;
    private readonly exportTypes: (keyof am4core.IExportOptions)[] = ['json', 'csv'];
    private _legendMaxHeightBeforeExport: number;
    private _legendScrollableBeforeExport: boolean;

    declare amchart: MapChart;
    declare options: MapChartOptionsModel;

    constructor(
        series: SeriesModel[],
        options: AbstractChartOptionsModel,
        metadata?: ChartMetadataModel
    ) {
        // Default noPadding for map charts
        super(series, {noPadding: true, ...options}, metadata);
    }

    public initChart(element: HTMLElement): void {
        super.initChart(element, this.chartType);

        this.amchart.htmlContainer.style.background = this.styles?.colorMap?.['backgroundColor'] || chartColors.agprWhite;
        this.amchart.htmlContainer.style.width = '99%';
        this.amchart.htmlContainer.style.padding = '15px';
        this.amchart.htmlContainer.style.borderRadius = '12px';
        this.amchart.chartContainer.wheelable = false;

        if (this.header && this.metadata) {
            this.header.background.fill = am4core.color(this.styles?.colorMap?.['backgroundColor'] || chartColors.agprWhite);
            if (this.options.noPadding) {
                this.header.padding(5, 5, 5, 5);
            }
            this.header.toFront();
            this.header.dom.id= 'headerId';
        }

        if (!this.options.geographyScale || !this.options.regionIsoCode) {
            this.setWorldSeries();
        }

        switch (this.options.geographyScale) {
            case GeographyTypesEnum.world:
                this.setWorldSeries();
                break;
            case GeographyTypesEnum.country:
                this.setCountrySeries();
                break;
        }

        if (!this.options.disableZoomControl) {
            this.buildZoomControl();
        }


        if (this.options.legendContainerElement) {
            this.initCustomLegend();
        } else {
            if (this.options.colorScheme === MapChartColorSchemeEnum.CHOROPLETH) {
                this.heatLegendContainer = this.amchart.createChild(am4core.Container);
                this.heatLegendContainer.width = am4core.percent(100);
                this.heatLegendContainer.height = 90;
                this.heatLegendContainer.valign = 'bottom';
                this.heatLegendContainer.layout = 'vertical';
                this.heatLegendContainer.background.fill = am4core.color(this.styles?.colorMap?.['backgroundColor'] || chartColors.agprWhite);

                this.heatLegendLabel = this.heatLegendContainer.createChild(am4core.Label);
                this.heatLegendLabel.text = this.options.choroplethLegendLabel;
                this.heatLegendLabel.fontSize = '0.875rem';
                this.heatLegendLabel.width = am4core.percent(60);
                this.heatLegendLabel.align = 'center';

                this.heatLegend = this.heatLegendContainer.createChild(HeatLegend);
                this.heatLegend.width = am4core.percent(60);
                this.heatLegend.align = 'center';
                this.heatLegend.minColor = am4core.color(chartColors.agprWhite);

                this.heatLegend.focusable = true;

            } else if (this.options.colorScheme === MapChartColorSchemeEnum.REGION_COLORS) {
                this.amchart.legend = new Legend();
                this.amchart.legend.scrollable = true;
                this.amchart.legend.scrollbar.dx = -15;
                this.amchart.legend.width = am4core.percent(100);
                this.amchart.legend.background.fill = am4core.color(this.styles?.colorMap?.['backgroundColor'] || chartColors.agprWhite);
                this.amchart.legend.fontSize = '0.875rem';
                this.amchart.legend.paddingTop = 10;
                this.amchart.legend.paddingLeft = 15;
                this.amchart.legend.paddingRight = 15;
                this.amchart.legend.paddingBottom = 15;
                this.amchart.legend.itemContainers.template.strokeOpacity = 0;
                this.amchart.legend.labels.template.text = '{name}';
                this.amchart.legend.itemContainers.template.interactionsEnabled = false;
                this.amchart.legend.itemContainers.template.paddingTop = 2;
                this.amchart.legend.itemContainers.template.paddingBottom = 2;
                this.amchart.legend.itemContainers.template.marginRight = 15;
                this.amchart.legend.contentAlign = 'left';
                this.amchart.legend.valign = 'bottom';
            }
        }

        if (this.options.colorScheme === MapChartColorSchemeEnum.CHOROPLETH) {
            this.styles.colorMap['choroplethHighValue'] = this.styles.colorMap['choroplethHighValue'] || chartColors.agprBlue;
            this.styles.colorMap['choroplethLowValue'] = this.styles.colorMap['choroplethLowValue'] || chartColors.agprLightBlue;
        }

        if (!this.options.responsive) {
            const reportStandardSize = ChartSizeHelper.getWidthAtMinCanvas(5, 2);
            this.amchart.events.on('sizechanged', ev => {
                const chart = ev.target;

                if (chart.pixelWidth <= reportStandardSize) {
                    if (this.options.colorScheme === MapChartColorSchemeEnum.CHOROPLETH) {
                        this.heatLegendLabel.fontSize = '0.75rem';
                    } else {
                        this.amchart.legend.fontSize = '0.75rem';
                    }
                    if (this.primarySeriesLabel) {
                        this.primarySeriesLabel.tooltip.dx = 0;
                    }
                    if (this.dateRangeLabel) {
                        this.dateRangeLabel.tooltipX = this.getTooltipTextWidth(this.dateRangeLabel.text?.length);
                    }
                } else {
                    if (this.options.colorScheme === MapChartColorSchemeEnum.CHOROPLETH) {
                        this.heatLegendLabel.fontSize = '0.875rem';
                    } else {
                        this.amchart.legend.fontSize = '0.813rem';
                    }
                    if (this.primarySeriesLabel) {
                        this.primarySeriesLabel.tooltip.dx = 50;
                    }
                    if (this.dateRangeLabel) {
                        this.dateRangeLabel.tooltipX = this.getTooltipTextWidth(this.dateRangeLabel.text?.length);
                    }
                }

                if (this.options.colorScheme === MapChartColorSchemeEnum.REGION_COLORS) {
                    if (chart.pixelHeight <= reportStandardSize) {
                        this.amchart.legend.maxHeight = 65;
                        if (this.options.legendScrollDisabled) {
                            this.amchart.legend.maxHeight = 210;
                        }
                    } else {
                        this.amchart.legend.maxHeight = 210;
                    }

                    if (chart.pixelWidth <= reportStandardSize) {
                        this.amchart.legend.markers.template.scale = 0.7;
                    } else {
                        this.amchart.legend.markers.template.scale = 1;
                    }

                    this.amchart.legend.afterDraw();
                }
            });
        }
    }

    protected updateCustomLegend(): void {
        super.updateCustomLegend();
        this.setCustomLegendElement(this.legendWrapper);
    }

    // we need to adjust the tooltip position so that it doesn't overflow the chart
    getTooltipTextWidth(length: number): number {
        let width = 0;
        if (!length) {
            return 120;
        }
        if (length < 10) {
            width = length;
        } else if (length < 20) {
            width = length + 60;
        } else if (length < 30) {
            width = length + 100;
        } else {
            width = length + 120;
        }
        return width;
    }

    getCustomLegendItems(): CustomLegendItem[] {
        const items: CustomLegendItem[] = [];
        if (this.options.colorScheme === MapChartColorSchemeEnum.REGION_COLORS) {
            const doCount: boolean = this.metadata?.labelDisplayOptions?.includes(LabelDisplayOptionsEnum.count);
            const doPercent: boolean = this.metadata?.labelDisplayOptions?.includes(LabelDisplayOptionsEnum.percent);

            this.amchart.series?.each(series => {
                if (!series || series.hiddenInLegend) {
                    return;
                }
                const polygon = (<MapPolygonSeries>series).mapPolygons.getIndex(0);
                const data = series.data[0];

                const item: CustomLegendItem = {
                    label: series.name,
                    color: series.fill.toString(),
                    mouseover: () => {
                        if (polygon) {
                            polygon.isHover = true;
                        }
                    },
                    mouseout: () => {
                        if (polygon) {
                            polygon.isHover = false;
                        }
                    },
                };
                if (data && doCount) {
                    item.count = '' + numberWithSeparators(data.value);
                }
                if (data && doPercent) {
                    item.percent = '' + Math.round(data.percent * 100) / 100;
                }
                items.push(item);
            });
        }

        return items;
    }

    setCustomLegendElement(element: HTMLElement): void {
        if (this.options.colorScheme === MapChartColorSchemeEnum.CHOROPLETH) {
            const item = document.createElement('div');
            const html = `
                <span>${this.options.choroplethLegendLabel}</span>
                <div style="width: 200px; height: 20px; margin: 3px 0;
                    background-image: linear-gradient(to right, ${this.styles.colorMap['choroplethLowValue']}, ${this.styles.colorMap['choroplethHighValue']})"
                ></div>
                <div style="width: 200px; display: flex; justify-content: space-between;">
                    <span>${numberWithSeparators(this.min)}</span>
                    <span>${numberWithSeparators(this.max)}</span>
                </div>
            `;

            item.innerHTML = html;
            element.append(item);
        }
    }

    /**
     * Sets the series data for a world map
     */
    private setWorldSeries(): void {
        this.amchart.geodata = am4geodata_worldLow;
        this.amchart.projection = new Mercator();

        // moves the "center" up, so that the legend doesn't overlap
        this.amchart.homeZoomLevel = 1;

        if (!this.options.legendContainerElement) {
            this.amchart.homeGeoPoint = {
                latitude: this.options.styles.initialLatitude ?? (this.options.colorScheme === MapChartColorSchemeEnum.CHOROPLETH ? 28 : 12),
                longitude: 0
            };
        }

        this.amchart.seriesContainer.resizable = false;

        let colorForOthers = this.styles?.colorMap?.other;
        if (!colorForOthers) {
            colorForOthers = chartColors.agprNotAvailable;
        }

        this.worldSeries = this.amchart.series.push(new MapPolygonSeries());
        this.worldSeries.name = 'world';
        this.worldSeries.useGeodata = true;
        this.worldSeries.exclude = ['AQ']; // exclude Antartica
        this.worldSeries.hiddenInLegend = true;
        this.worldSeries.mapPolygons.template.nonScalingStroke = true;
        this.worldSeries.fill = am4core.color(colorForOthers);
        this.worldSeries.mapPolygons.template.fill = this.worldSeries.fill;
        // need to set focusable to false or else blank countries will also be in the tab order
        this.worldSeries.mapPolygons.template.focusable = false;
    }

    /**
     * Sets the series data for a specific country
     */
    private setCountrySeries(): void {
        const isoCodeModel = mapChartAvailableCountryIsoCodes[this.options.regionIsoCode];
        let homeGeoPoint;
        if (isoCodeModel) {
            homeGeoPoint = JSON.parse(JSON.stringify(isoCodeModel.homeGeoPoint));
            if (this.options.colorScheme === MapChartColorSchemeEnum.CHOROPLETH && homeGeoPoint) {
                homeGeoPoint.latitude += 3 / (isoCodeModel.homeZoomLevel * 3 || 1);
            }
        } else {
            this.setWorldSeries();
            return;
        }
        this.countryGeoData = isoCodeModel.amchartsGeodata[0];
        if (isoCodeModel.projection) {
            this.amchart.projection = isoCodeModel.projection;
        }

        if (!this.options.legendContainerElement) {
            // moves the "center" up, so that the legend doesn't overlap
            this.amchart.homeGeoPoint = homeGeoPoint;
            this.amchart.homeZoomLevel = isoCodeModel.homeZoomLevel || 1;
            if (isoCodeModel.start && isoCodeModel.end) {
                // adds hidden lines on the map to set the size of viewable area
                this.countrySpaceSeries = this.amchart.series.push(new MapLineSeries());
                this.countrySpaceSeries.useGeodata = false;
                this.countrySpaceSeries.ignoreBounds = false;
                this.countrySpaceSeries.hiddenInLegend = true;
                this.countrySpaceSeries.fill = am4core.color(chartColors.agprWhite);
                this.countrySpaceSeries.mapLines.template.fill = this.countrySpaceSeries.fill;
                this.countrySpaceSeries.mapLines.template.stroke = this.countrySpaceSeries.fill;
                this.countrySpaceSeries.mapLines.create();
                this.countrySpaceSeries.mapLines.getIndex(0).multiGeoLine = [
                    [
                        isoCodeModel.start,
                        isoCodeModel.end
                    ]
                ];
                // Hide it or else it can show up in the PDF export
                this.countrySpaceSeries.hide();
            }
        }
        this.amchart.draggable = false;

        let colorForOthers = this.styles?.colorMap?.other;
        if (!colorForOthers) {
            colorForOthers = chartColors.agprNotAvailable;
        }

        this.countrySeries = this.amchart.series.push(new MapPolygonSeries());
        this.countrySeries.name = 'country';
        this.countrySeries.geodata = this.countryGeoData;
        this.countrySeries.useGeodata = true;
        this.countrySeries.hiddenInLegend = true;
        this.countrySeries.mapPolygons.template.nonScalingStroke = true;
        this.countrySeries.draggable = false;
        this.countrySeries.fill = am4core.color(colorForOthers);
        this.countrySeries.mapPolygons.template.fill = this.countrySeries.fill;
        this.countrySeries.mapPolygons.template.polygon.fillOpacity = 0;
        this.countrySeries.mapPolygons.template.polygon.strokeOpacity = 0;
        // need to set focusable to false or else blank areas will also be in the tab order
        this.countrySeries.mapPolygons.template.focusable = false;
    }

    /**
     * Build the zoom controls (zoom in/out and home button)
     *
     * This is used for the initial build of the chart and after exporting the chart
     */
    private buildZoomControl() {
        this.amchart.zoomControl = new ZoomControl();
        this.amchart.zoomControl.exportable = false;
        this.amchart.zoomControl.valign = 'top';
        this.amchart.zoomControl.dy = 73;

        this.associateHomeButton();
    }

    private associateHomeButton() {
        this.homeButton = new am4core.Button();
        this.homeButton.readerTitle = 'Home';
        this.homeButton.events.on('hit', () => {
            this.amchart.goHome();
        });

        this.homeButton.icon = new am4core.Sprite();
        this.homeButton.padding(7, 0, 10, 0);
        this.homeButton.width = 30;
        this.homeButton.contentValign = 'middle';
        this.homeButton.icon.path = 'M16,8 L14,8 L14,16 L10,16 L10,10 L6,10 L6,16 L2,16 L2,8 L0,8 L8,0 L16,8 Z M16,8';
        this.homeButton.marginBottom = 10;

        this.homeButton.parent = this.amchart.zoomControl;

        this.homeButton.insertBefore(this.amchart.zoomControl.plusButton);
    }

    /**
     * Updates all of the output series when data changes
     */
    public setOutputSeries(): void {
        if (this.outputSeries) {
            this.outputSeries.forEach((amseries) => {
                this.amchart?.series.removeValue(amseries);
            });
        }
        this.outputSeries = [];
        if (!this.amchart?.data) {
            return;
        }

        if (this.options.colorScheme === MapChartColorSchemeEnum.CHOROPLETH) {
            const dataPoints = [].concat(...this.rawData).filter(x => !!x.id);
            if (dataPoints.length) {
                this.min = Math.min(...dataPoints.map(x => (x.value || 0)));
                this.max = Math.max(...dataPoints.map(x => (x.value || 0)));
            } else {
                this.min = 0;
                this.max = 0;
            }

            if (this.heatLegend) {
                this.heatLegend.minColor = am4core.color(this.styles.colorMap['choroplethLowValue']);
                this.heatLegend.maxColor = am4core.color(this.styles.colorMap['choroplethHighValue']);
                this.heatLegend.minValue = this.min;
                this.heatLegend.maxValue = this.max;
                this.heatLegend.readerTitle = `${this.options.choroplethLegendLabel} scale: min: ${this.min}, max: ${this.max}`;

                this.heatLegend.valueAxis.axisRanges.clear();
                const minRange = this.heatLegend.valueAxis.axisRanges.create();
                minRange.value = this.min;
                minRange.label.text = '' + this.heatLegend.numberFormatter.format(this.min);
                const maxRange = this.heatLegend.valueAxis.axisRanges.create();
                maxRange.value = this.max;
                maxRange.label.text = '' + this.heatLegend.numberFormatter.format(this.max);
                this.heatLegend.valueAxis.logarithmic = this.options.interpolationType === MapChartChoroplethInterpolationTypeEnum.LOGARITHMIC;

                this.heatLegend.valueAxis.renderer.labels.template.adapter.add('text', (labelText) => {
                    return '';
                });
            }
        }

        const colors: chartColors[] = defaultSeriesColorConfig;
        let otherCount = 0;
        let otherPercent = 0;
        for (const seriesData of this.rawData) {
            if (!seriesData) {
                return;
            }

            for (const [ index, dataPoint ] of seriesData.entries()) {
                let maxSeries: number;
                if (this.options.colorScheme === MapChartColorSchemeEnum.CHOROPLETH) {
                    maxSeries = 100;
                } else if (this.options.colorScheme === MapChartColorSchemeEnum.REGION_COLORS) {
                    maxSeries = 10;
                }
                // Country based chart, for mentions that do not have province info, we set its id to null, and this should be added to other legend
                if ((seriesData.length > maxSeries && index >= maxSeries) || dataPoint.id === null ) {
                    otherCount += dataPoint.value;
                    otherPercent += (dataPoint.percent as number) || 0;
                    continue;
                }

                const color = this.styles.colorMap[`region${index + 1}`] || colors[index];
                this.addOutputSeries(dataPoint, am4core.color(color));
            }
        }

        if (otherCount > 0) {
            this.addOutputSeries({
                label: this.options.otherText,
                value: otherCount,
                percent: otherPercent,
            }, am4core.color(this.styles.colorMap['other'] || chartColors.agprNotAvailable));
        }

        this.ready.next(true);
    }

    /**
     * Adds an output series for highlighting a country
     */
    public addOutputSeries(dataPoint: DataPointModel, color: am4core.Color): void {
        if (!dataPoint['value0']) {
            return;
        }
        const outputSeries = this.amchart.series.push(new MapPolygonSeries());
        outputSeries.name = dataPoint.label;
        outputSeries.fill = color;
        if (this.countryGeoData) {
            outputSeries.geodata = this.countryGeoData;
        }
        outputSeries.draggable = false;
        outputSeries.useGeodata = true;
        outputSeries.ignoreBounds = true;
        // outputSeries.mapPolygons.template.strokeWidth = 0.5;
        if (dataPoint.id) {
            outputSeries.include = [dataPoint.id];
            outputSeries.data = [JSON.parse(JSON.stringify(dataPoint))];

        } else {
            // don't show anything for "Other" in the chart, but it needs to be included in the legend
            outputSeries.include = [];
        }

        let srText = '{name}, ';
        if (this.options.colorScheme === MapChartColorSchemeEnum.CHOROPLETH) {
            outputSeries.dataFields.value = 'value';
            if (outputSeries.data?.length > 0) {
                outputSeries.heatRules.push({
                    property: 'fill',
                    target: outputSeries.mapPolygons.template,
                    min: am4core.color(this.styles.colorMap['choroplethLowValue']),
                    max: am4core.color(this.styles.colorMap['choroplethHighValue']),
                    minValue: this.min,
                    maxValue: this.max,
                    logarithmic: this.options.interpolationType === MapChartChoroplethInterpolationTypeEnum.LOGARITHMIC
                });
            }
        } else if (this.options.colorScheme === MapChartColorSchemeEnum.REGION_COLORS) {
            // make a copy of the data point so amcharts doesn't mess with the original
            outputSeries.mapPolygons.template.fill = outputSeries.fill;

            if (this.amchart.legend) {
                this.amchart.legend.valueLabels.template.align = 'left';
                this.amchart.legend.valueLabels.template.textAlign = 'start';
            }

            outputSeries.legendSettings.labelText = '{name}';
            // Text to use as aria-label for each output
            if (this.metadata?.labelDisplayOptions) {

                const doCount: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.count);
                const doPercent: boolean = this.metadata.labelDisplayOptions.includes(LabelDisplayOptionsEnum.percent);
                const countFormatString: string = `[bold]${dataPoint.value}[/]`;
                const percentFormatString: string = `[bold]${Math.round(+dataPoint.percent * 100) / 100}%[/]`;

                if (doCount) {
                    outputSeries.legendSettings.labelText += ' -';
                    if (doPercent) {
                        outputSeries.legendSettings.valueText = `${countFormatString}, ${percentFormatString}`;
                        srText += `${dataPoint.value}, ${Math.round(+dataPoint.percent * 100) / 100}%`;
                    } else {
                        outputSeries.legendSettings.valueText = `${countFormatString}`;
                        srText += `${dataPoint.value}`;
                    }
                } else if (doPercent) {
                    outputSeries.legendSettings.labelText += ' -';
                    outputSeries.legendSettings.valueText = `${percentFormatString}`;
                    srText += `${Math.round(+dataPoint.percent * 100) / 100}%`;
                } else {
                    outputSeries.legendSettings.valueText = ' ';
                }

            // this is necessary to reset the value in some situations
            } else {
                outputSeries.legendSettings.labelText = '{name}';
                outputSeries.legendSettings.valueText = ' ';
                srText = '{name}, ';
            }
        }
        if (this.countrySeries) {
            this.countrySeries.mapPolygons.template.polygon.fillOpacity = 1;
            this.countrySeries.mapPolygons.template.polygon.strokeOpacity = 1;
        }
        outputSeries.mapPolygons.template.nonScalingStroke = true;
        outputSeries.readerTitle = srText;

        // this stuff is useful for chart segment interaction, but
        // works nice even with the non-interactable tooltips, so set them here instead of in ReportsChart
        outputSeries.calculateVisualCenter = true;
        outputSeries.tooltip.keepTargetHover = true;
        outputSeries.mapPolygons.template.tooltipPosition = 'fixed';
        // set tooltip style
        outputSeries.tooltip.getFillFromObject = false;
        outputSeries.tooltip.background.fill = am4core.color(chartColors.agprWhite);
        outputSeries.tooltip.background.stroke = am4core.color('#ced4da');
        outputSeries.tooltip.background.cornerRadius = 8;
        outputSeries.tooltip.background.fillOpacity = 1;
        // clear tooltip background shadow
        outputSeries.tooltip.background.filters.clear();
        outputSeries.tooltip.label.padding(5, 10, 5, 10);
        outputSeries.tooltip.label.fill = am4core.color(chartColors.agprBlack);
        outputSeries.mapPolygons.template.tooltipText = this.options.hideTooltip ? '' :
            `{name} - ${numberWithSeparators(dataPoint.value)} - [bold]${Math.round(+dataPoint.percent * 100) / 100}%[/]`;

        this.outputSeries.push(outputSeries);
    }
    /**
     * Updates the current presentation of data in the chart
     */
    public updateData(data: DataPointModel[][]): void {
        this.rawData = data;

        // The map needs to put the data onto the individual series instead of onto the global data object, so skip assigning it to data here
        this.updateSeriesStyles();

        if (!data?.includes(null) && this.isContentEmpty()) {
            this.amchart.seriesContainer.disabled = true;
            this.showNoContentMessage();
        } else if (data.some(subData => subData.some(dateItem => !!dateItem.filterErrorMessage))){
            this.amchart.seriesContainer.disabled = true;
            let filterErrorMessage;
            data.forEach(subData => subData.forEach(dataItem => {
                if (dataItem.filterErrorMessage) {
                    filterErrorMessage = dataItem.filterErrorMessage;
                    return;
                }
            }));
            this.showNoContentMessage(filterErrorMessage);
        } else {
            if (this.amchart) {
                this.amchart.seriesContainer.disabled = false;
            }
            if (this.heatLegend) {
                this.heatLegendLabel.opacity = 1;
                this.heatLegend.opacity = 1;
            }
        }
    }

    public showNoContentMessage(message?: string): void {
        super.showNoContentMessage(message);
        if (this.heatLegend) {
            this.heatLegendLabel.opacity = 0;
            this.heatLegend.opacity = 0;
        }
    }

    public updateSeriesStyles(): void {
        this.setOutputSeries();
    }

    /**
     * Modifies the chart to have the right styles and be the right dimensions for export
     */
    public setExportStyles(scale?: number, keepFontsize?: boolean): void {
        this.amchart?.events.disableType('sizechanged');

        super.setExportStyles(scale, keepFontsize);

        // Override the base height (600*scale) to give enough room for the full legend
        if (this.amchart?.htmlContainer && scale) {
            this.amchart.htmlContainer.style.height = (800 * scale) + 'px';
        }

        if (this.amchart) {
            this.amchart.htmlContainer.style.background = this.styles?.colorMap?.['backgroundColor'] || chartColors.agprWhite;
        }

        if (this.amchart?.legend) {
            this.amchart.legend.fontSize = 14;
            // Save and remove scroll constraints so the full legend is captured in the PNG export
            this._legendScrollableBeforeExport = this.amchart.legend.scrollable;
            this._legendMaxHeightBeforeExport = this.amchart.legend.maxHeight;
            this.amchart.legend.scrollable = false;
            this.amchart.legend.maxHeight = undefined;
            // amcharts sets a maskRectangle on each itemContainer when scrollable is active.
            // Disabling scroll doesn't clear those masks, so hidden items remain clipped.
            // We must clear them manually to make all items visible for export.
            this.amchart.legend.itemContainers.each(itemContainer => {
                itemContainer.dy = 0;
                itemContainer.maskRectangle = undefined;
            });
        }

        if (this.heatLegend) {
            this.amchart?.series?.each(s => {
                s.dy = 5;
            });
            this.heatLegendLabel.dy = 10;
            this.heatLegend.dy = 10;
        }
    }

    /**
     * Undoes the changes made in setExportStyles to make the chart normal again
     */
    public unsetExportStyles(): void {
        if (this.amchart?.legend) {
            this.amchart.legend.fontSize = '0.75rem';
            // Revert the scroll constraints back to what they were before export
            this.amchart.legend.scrollable = this._legendScrollableBeforeExport;
            this.amchart.legend.maxHeight = this._legendMaxHeightBeforeExport;
        }

        if (this.header) {
            this.header.dy = 0;
            this.header.background.fill = am4core.color(chartColors.agprWhite);
        }

        if (this.heatLegend) {
            this.amchart?.series?.each(s => {
                s.dy = 0;
            });
            this.heatLegendLabel.dy = 0;
            this.heatLegend.dy = 0;
        }

        super.unsetExportStyles();

        // Re-enable sizechanged after the container is back to normal size
        this.amchart?.events.enableType('sizechanged');
    }

    /**
     * Overrides the export chart feature.  Implements custom download for csv & json formats
     */
    public export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {
        const dataFields = {
            label: this.options.label,
            value: this.options.xValueLabel,
            percent: this.options.percentLabel
        };

        this.amchart.exporting.dataFields = dataFields;
        this.amchart.exporting.filePrefix = filename;
        this.amchart.exporting.useWebFonts = false;

        const exportType = this.exportTypes.find(exportOption => exportOption === type);
        // necessary to have a manual formatting and download for csv & json export
        // because am charts MapChart breaks when it has data through the usual data object
        if (exportType) {
            manualChartExport.exportFlatSeriesData(exportType, this.rawData[0], dataFields, filename);
            return;
        }

        return this.amchart.exporting.export(type);
    }

    public isContentEmpty(): boolean {
        return !(this.rawData?.[0]?.length);
    }

    public clone(): MapChartModel {
        return new MapChartModel(
            this.series.map(s => s.clone()),
            this.options,
            this.metadata
        );
    }
}
